/**
 * useLoadingGate — lifecycle del splash de loading premium.
 *
 * Patrón compartido entre BrandSplashScreen (App.tsx) y TemplateLoadingScreen
 * (Index.tsx). Garantiza 3 cosas:
 *
 *   1. MIN_SHOW_MS (350ms): si la data carga en <100ms, igual mantiene el
 *      splash visible para evitar flicker. Un splash de 30ms se ve peor que
 *      ninguno (parece un glitch).
 *
 *   2. FADE_OUT_MS (280ms): cuando isLoading pasa a false, en lugar de
 *      unmount inmediato, arranca un fade-out smooth con opacity 1→0. El
 *      content se renderiza en paralelo debajo (caller decide).
 *
 *   3. Lifecycle states:
 *      - `splashVisible`: mostrar el splash en el DOM
 *      - `fadingOut`: aplicar opacity transition (caller pasa como prop)
 *      - `contentReady`: renderizar el content (caller puede mostrar mientras
 *        el splash está fading-out → transición seamless)
 *
 * Uso típico:
 *
 *   const { splashVisible, fadingOut, contentReady } = useLoadingGate(isLoading);
 *   return (
 *     <>
 *       {splashVisible && <TemplateLoadingScreen fadingOut={fadingOut} />}
 *       {contentReady && <YourContent />}
 *     </>
 *   );
 *
 * Caller siempre renderiza children primero — el splash queda encima por
 * z-index. Cuando arranca el fade-out, pointer-events:none permite que el
 * usuario interactúe con el content debajo aunque el splash siga visible
 * por unos ms.
 */

import { useEffect, useRef, useState } from "react";

export const LOADING_GATE_MIN_SHOW_MS = 350;
export const LOADING_GATE_FADE_OUT_MS = 280;

export interface LoadingGateState {
  /** El splash debe estar montado en el DOM (con opacity full o fading). */
  splashVisible: boolean;
  /** El splash debe animar opacity 1→0 (pasarle como prop fadingOut). */
  fadingOut: boolean;
  /** El content puede renderearse (en paralelo durante fade, o solo después). */
  contentReady: boolean;
}

export function useLoadingGate(isLoading: boolean): LoadingGateState {
  const [splashVisible, setSplashVisible] = useState(true);
  const [fadingOut, setFadingOut] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const mountedAt = useRef<number>(Date.now());

  useEffect(() => {
    if (isLoading) return;
    // Min show time: si la data cargó muy rápido, esperamos a cumplir
    // LOADING_GATE_MIN_SHOW_MS antes de empezar el fade-out.
    const elapsed = Date.now() - mountedAt.current;
    const waitBeforeFade = Math.max(0, LOADING_GATE_MIN_SHOW_MS - elapsed);

    // Tras waitBeforeFade: marcamos content listo (renderiza en paralelo) y
    // arrancamos fade-out del splash.
    const readyTimer = setTimeout(() => {
      setContentReady(true);
      setFadingOut(true);
    }, waitBeforeFade);

    // Tras waitBeforeFade + FADE_OUT_MS: unmount del splash.
    const unmountTimer = setTimeout(() => {
      setSplashVisible(false);
    }, waitBeforeFade + LOADING_GATE_FADE_OUT_MS);

    return () => {
      clearTimeout(readyTimer);
      clearTimeout(unmountTimer);
    };
  }, [isLoading]);

  return { splashVisible, fadingOut, contentReady };
}
